import { gql, useQuery } from '@apollo/client';
import {
	EcsGetUserDetails_users_nodes as UserDetails,
	EcsGetUserDetails_users_nodes_accounts as UserAccountDetails,
	EditAccountScreenGetAccountDetails_accounts_nodes as AccountDetails,
	EditAccountScreenGetAccountDetails_accounts_nodes_user as AccountUserDetails,
	EmailDeliveryStatusFilterInput,
	EmailDeliveryStatusSortInput,
	OrganismsEcGetEmails,
	OrganismsEcGetEmails_emailDeliveryStatuses_nodes as EmailDeliveryStatus,
	OrganismsEcGetEmailsVariables,
	SortEnumType,
	SystemRole,
} from '@app/codegen';
import { EmailsComponentLocators } from '@app/e2e/shared/Emails';
import {
	CustomCard,
	CustomDataTable,
	ITrustAction,
	ITrustColumn,
	useTableState,
} from '@app/shared/components';
import { isFormEmpty } from '@app/shared/helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppIcon, localDateTimeFormat, useAppState } from '@itrustcapital/ui';
import { Button, Input, Text } from '@ui-kitten/components';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

import { EmailModal, EmailModalMode } from './EmailModal';

export interface EmailsProps {
	accountDetails?: UserAccountDetails | AccountDetails | null | undefined;
	userDetails: UserDetails | AccountUserDetails | null | undefined;
	fundingId?: number;
}

export const ORGANISMS_EC_GET_EMAILS = gql`
	query OrganismsEcGetEmails(
		$where: EmailDeliveryStatusFilterInput
		$order: [EmailDeliveryStatusSortInput!]
		$first: Int
		$last: Int
		$after: String
		$before: String
	) {
		emailDeliveryStatuses(
			where: $where
			order: $order
			first: $first
			last: $last
			after: $after
			before: $before
		) {
			nodes {
				id
				status
				emailAddressType
				emailAddress
				sentDate
				deliveredDate
				failedDate
				failureReason
				email {
					id
					subject
					body
					template
					templateId
					from
					sentByUserId
					sentByRole
					userId
					accountId
					fundingId
					createdAt
				}
			}
			pageInfo {
				startCursor
				endCursor
			}
			totalCount
		}
	}
`;

const schema = yup.object({ search: yup.string().default('') }).defined();

export type EmailSearchFormData = yup.TypeOf<typeof schema>;

export function Emails(props: EmailsProps) {
	const styles = useCustomStyles();

	const tableState = useTableState<EmailDeliveryStatusSortInput, EmailDeliveryStatusFilterInput>({
		defaultSort: { createdAt: SortEnumType.DESC },
		defaultWhere: undefined,
		defaultRowsPerPage: 5,
		pagination: true,
		striped: true,
	});

	const newEmailMessageModal = useAppState(false);
	const newEmailMessageModalMode = useAppState<EmailModalMode>(null);
	const selectedEmailId = useAppState<number | null>(null);

	const form = useForm<EmailSearchFormData>({
		mode: 'all',
		criteriaMode: 'all',
		resolver: yupResolver(schema),
		defaultValues: schema.cast({}),
	});

	const queryVariables = useMemo(() => {
		let whereClause = { ...tableState.where.get };

		if (props.fundingId) {
			whereClause = {
				...whereClause,
				email: {
					...whereClause.email,
					fundingId: { eq: props.fundingId },
				},
			};
		} else if (props.accountDetails?.id) {
			whereClause = {
				...whereClause,
				email: {
					...whereClause.email,
					accountId: { eq: props.accountDetails.id },
				},
			};
		} else if (props.userDetails?.id) {
			whereClause = {
				...whereClause,
				or: [
					{
						emailAddress: { eq: props.userDetails.email },
					},
					{
						email: {
							userId: { eq: props.userDetails.id },
						},
					},
				],
			};
		}

		return {
			order: tableState.sort.get,
			first: tableState.rowsPerPage.get,
			where: whereClause,
		};
	}, [
		tableState.sort.get,
		tableState.rowsPerPage.get,
		tableState.where.get,
		props.fundingId,
		props.accountDetails?.id,
		props.userDetails?.id,
		props.userDetails?.email,
	]);

	const getEmailsQuery = useQuery<OrganismsEcGetEmails, OrganismsEcGetEmailsVariables>(
		ORGANISMS_EC_GET_EMAILS,
		{
			variables: queryVariables,
			skip: !tableState.where.get,
			fetchPolicy: 'network-only',
			onCompleted: onGetEmailCompleted,
		}
	);

	const columns: ITrustColumn<EmailDeliveryStatus>[] = [
		{
			key: 'status',
			name: 'Status',
			maxWidth: 150,
			sortable: true,
			selector: (row) => row.status,
		},
		{
			key: 'sentByUserId',
			name: 'Sent By ID',
			maxWidth: 100,
			sortable: true,
			selector: (row) => row.email?.sentByUserId || '--',
		},
		{
			key: 'sentByRole',
			name: 'Sent By Role',
			maxWidth: 150,
			sortable: true,
			selector: (row) => row.email?.sentByRole || 'System',
		},
		{
			key: 'subject',
			name: 'Subject',
			minWidth: 400,
			sortable: true,
			selector: (row) => row.email?.subject,
		},
		{
			key: 'template',
			name: 'Template',
			minWidth: 400,
			sortable: true,
			selector: (row) => row.email?.template,
		},
		{
			key: 'templateId',
			name: 'Template Id',
			minWidth: 400,
			sortable: true,
			selector: (row) => row.email?.templateId,
		},
		{
			key: 'from',
			name: 'Sender',
			minWidth: 400,
			sortable: false,
			selector: (row) => row.email?.from,
		},
		{
			key: 'emailAddress',
			name: 'Recipient',
			minWidth: 400,
			sortable: false,
			selector: (row) => `(${row.emailAddressType}) ${row.emailAddress}`,
		},
		{
			key: 'createdAt',
			name: 'Created Date',
			minWidth: 400,
			sortable: false,
			selector: (row) => localDateTimeFormat(row.email?.createdAt),
		},
		{
			key: 'sentDate',
			name: 'Sent Date',
			minWidth: 400,
			sortable: false,
			selector: (row) => localDateTimeFormat(row.sentDate),
		},
		{
			key: 'deliveredDate',
			name: 'Delivered Date',
			minWidth: 400,
			sortable: false,
			selector: (row) => localDateTimeFormat(row.deliveredDate),
		},
		{
			key: 'failedDate',
			name: 'Failed Date',
			minWidth: 400,
			sortable: false,
			selector: (row) => localDateTimeFormat(row.failedDate),
		},
	];

	const actions: ITrustAction<EmailDeliveryStatus>[] = [
		{
			name: 'View',
			handler: onView,
			iconLib: 'ion',
			iconName: 'eye',
		},
	];

	function makeDefaultWhereFilter() {
		const whereFilter: EmailDeliveryStatusFilterInput = {};

		if (!props.fundingId && !props.accountDetails?.id && !props.userDetails?.id) {
			return whereFilter;
		}

		if (props.fundingId) {
			whereFilter.email = {
				fundingId: {
					eq: props.fundingId,
				},
			};

			return whereFilter;
		}

		if (props.accountDetails?.id) {
			whereFilter.email = {
				accountId: {
					eq: props.accountDetails.id,
				},
			};

			return whereFilter;
		}

		// we search for both email or user ID since their email can change with time
		whereFilter.or = [
			{
				emailAddress: {
					eq: props.userDetails!.email,
				},
			},
			{
				email: {
					userId: {
						eq: props.userDetails!.id,
					},
				},
			},
		];

		return whereFilter;
	}

	function onRefetch(where: EmailDeliveryStatusFilterInput) {
		getEmailsQuery?.refetch?.({
			after: undefined,
			before: undefined,
			last: undefined,
			first: tableState.rowsPerPage.get,
			order: [{ createdAt: SortEnumType.DESC }],
			where,
		});

		tableState.reset();
	}

	function onGetEmailCompleted(data: OrganismsEcGetEmails): void {
		if (data) {
			tableState.onCompleted(
				data.emailDeliveryStatuses?.totalCount,
				data.emailDeliveryStatuses?.pageInfo
			);
		}
	}

	function onView(rowItem: EmailDeliveryStatus): void {
		newEmailMessageModal.set(!newEmailMessageModal.get);
		newEmailMessageModalMode.set('View');
		selectedEmailId.set(rowItem.email?.id!);
	}

	function onSearch(data: EmailSearchFormData): void {
		const whereFilter: EmailDeliveryStatusFilterInput = makeDefaultWhereFilter();

		tableState.reset();

		if (isFormEmpty(data)) {
			tableState.where.set(whereFilter);

			return;
		}

		const orFilter: EmailDeliveryStatusFilterInput[] = [
			{
				email: {
					subject: {
						contains: data.search,
					},
				},
			},
		];

		const searchTerms = data.search.split(' ');
		for (const searchTerm of searchTerms) {
			if (+searchTerm) {
				orFilter.push({
					email: {
						sentByUserId: {
							eq: +searchTerm,
						},
					},
				});
			}

			if (Object.values<string>(SystemRole).includes(searchTerm)) {
				orFilter.push({
					email: {
						sentByRole: {
							eq: SystemRole[searchTerm as keyof typeof SystemRole],
						},
					},
				});
			}
		}

		// need to handle the user page where we search by user ID or email
		if (whereFilter.or) {
			whereFilter.and = [
				{
					or: whereFilter.or,
				},
				{
					or: orFilter,
				},
			];
			delete whereFilter.or;
		} else {
			whereFilter.or = orFilter;
		}

		onRefetch(whereFilter);
		tableState.where.set(whereFilter);
	}

	function onToggleModal(): void {
		selectedEmailId.set(null);
		newEmailMessageModal.set(!newEmailMessageModal.get);
	}

	function onSaved(success: boolean): void {
		if (success) {
			onToggleModal();
			onRefetch(tableState.where.get!);
		}

		Toast.show({
			type: success ? 'success' : 'error',
			text2: success ? 'Email has been sent.' : 'Email was unable to be sent.',
		});
	}

	return (
		<CustomCard
			header={() => (
				<View style={styles.main.header}>
					<Text category="h6">Emails & Tickets</Text>
					<View style={styles.actions.container}>
						<Button
							testID={EmailsComponentLocators.emailsNewButton}
							onPress={() => {
								newEmailMessageModalMode.set('New');
								newEmailMessageModal.set(!newEmailMessageModal.get);
							}}
						>
							New Message
						</Button>
					</View>
				</View>
			)}
		>
			<EmailModal
				userDetails={props.userDetails! as UserDetails}
				accountDetails={props.accountDetails! as UserAccountDetails}
				fundingId={props.fundingId}
				id={selectedEmailId.get!}
				mode={newEmailMessageModalMode.get}
				toggleVisible={onToggleModal}
				visible={newEmailMessageModal.get}
				onSaved={onSaved}
			/>

			<Controller
				control={form.control}
				name="search"
				render={(control) => (
					<Input
						accessoryRight={() => (
							<TouchableOpacity
								testID={EmailsComponentLocators.emailSerchIconButton}
								onPress={form.handleSubmit(onSearch)}
							>
								<AppIcon lib="fe" name="search" />
							</TouchableOpacity>
						)}
						placeholder="Search for email by subject or created by"
						testID={EmailsComponentLocators.emailsSearchField}
						value={control.field.value!}
						onBlur={control.field.onBlur}
						onChangeText={control.field.onChange}
						onSubmitEditing={form.handleSubmit(onSearch)}
					/>
				)}
			/>
			<CustomDataTable
				columns={columns}
				data={getEmailsQuery.data?.emailDeliveryStatuses?.nodes as EmailDeliveryStatus[]}
				progressPending={getEmailsQuery.loading}
				refetch={(variables) =>
					getEmailsQuery.refetch({
						...queryVariables,
						...variables,
					})
				}
				rowActions={actions}
				{...tableState.props}
			/>
		</CustomCard>
	);
}

function useCustomStyles() {
	return {
		main: StyleSheet.create({
			header: {
				flexDirection: 'row',
				alignItems: 'center',
			},
		}),
		actions: StyleSheet.create({
			container: {
				flexDirection: 'row',
				justifyContent: 'flex-end',
				flex: 1,
			},
		}),
	};
}
